import type { DateColumn } from './order-proposal-math'
import { formatStockDisplay } from '@/shared/lib/stock-quantity'

/**
 * How the Bestellvorschlag names days and quantities, on screen, on paper and in the CSV
 * (CHG-2026-10-bestellvorschlag).
 */

const weekdayFormat = new Intl.DateTimeFormat('de-AT', {
  weekday: 'short',
  timeZone: 'UTC',
})

function parts(date: string) {
  const [year, month, day] = date.split('-')
  return { year, month, day }
}

/** "Mo 28.09." — with `withYear` "Mo 28.09.2026". */
export function formatDay(date: string, withYear = false): string {
  const { year, month, day } = parts(date)
  // Noon UTC with a UTC formatter: the calendar day as written, whatever the viewer's zone.
  const weekday = weekdayFormat
    .format(new Date(`${date}T12:00:00Z`))
    .replace(/\.$/, '')
  return `${weekday} ${day}.${month}.${withYear ? year : ''}`
}

/** "22.09. – 30.09." */
function formatShortDate(date: string, withYear: boolean): string {
  const { year, month, day } = parts(date)
  return `${day}.${month}.${withYear ? year : ''}`
}

/** A date column as its header shows it: one day with its weekday, a range as von – bis. */
export function formatColumn(column: DateColumn, withYear = false): string {
  if (column.from === column.to) return formatDay(column.from, withYear)
  return `${formatShortDate(column.from, withYear)} – ${formatShortDate(column.to, withYear)}`
}

/**
 * A planned Tagesgericht's order days, compactly: "Fr, Sa" — with the date when a weekday occurs
 * twice in the order days ("Mo 05.10., Mo 12.10.").
 */
export function formatOrderDays(days: Array<string>): string {
  const weekdays = days.map((day) => formatDay(day).split(' ')[0])
  return new Set(weekdays).size === weekdays.length
    ? weekdays.join(', ')
    : days.map((day) => formatDay(day)).join(', ')
}

/** A quantity as the inventory page shows it: whole numbers plain, decimals with the comma. */
export function formatQuantity(value: number): string {
  return formatStockDisplay(Math.round(value * 1000) / 1000)
}

type Translate = (key: 'orderProposal.today' | 'orderProposal.before') => string

/** "heute · zuvor Di 29.09." / "Mi 30.09." — when a sold Tagesgericht ran. */
export function dishDayLabel(
  t: Translate,
  dish: { today: boolean; previousDays: Array<string> },
): string {
  const previous = dish.previousDays.map((day) => formatDay(day)).join(', ')
  if (!dish.today) return previous
  return previous
    ? `${t('orderProposal.today')} · ${t('orderProposal.before')} ${previous}`
    : t('orderProposal.today')
}
